import React from 'react';
import Phrase from './Phrase';
import FaVolumeUp from 'react-icons/lib/fa/volume-up';
import {getUserData} from "../../services/API";
import {parseSkipTranslationTags, stringContainsHumanPhoneme} from "../../services/util";
import { isMarkupInstructionForStory, playMarkupSequence, getStoryContentLocale } from "../../services/playMarkupSequence";
import { logSessionInfo } from "../../services/logger";
import classNames from 'classnames';
import { getMetadataType } from './ReaderUtils';
import get from 'lodash.get';
import VirtualScreen from './VirtualScreen/VirtualScreen'
import NextPageButton from './VirtualScreen/NextPageButton';
import NextButton from './NextButton';
import Branding from './Branding/Branding';
import ReaderVideoPlayer from './Video/ReaderVideoPlayer';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";

export default class Reader extends React.Component {
  constructor(props) {
    super(props);
    this.state={
      isRepeating: false,  // whether or not we are currently repeating the phrase instructions. This is here instead of in phrase because the repeat button lives outside of the phrase element, in the broader "Reader" container
      hideRepeatButton: false,
      hideListeningStatus: false,
      repeatButtonDisabled: false,
    }
    this.getCurrentPhraseMetaData = this.getCurrentPhraseMetaData.bind(this);
  }

  componentDidUpdate(prevProps){
    if(prevProps.repeatInstructions !== this.props.repeatInstructions){
      this.setState({isRepeating: false});
    }
  }

  setHideRepeatButton(hideRepeatButton) {
    this.setState({hideRepeatButton});
  }

  setHideListeningStatus(hideListeningStatus) {
    this.setState({hideListeningStatus});
  }

	disableRepeatButton(){
		this.setState({repeatButtonDisabled: true});
	}

	enableRepeatButton(){
		this.setState({repeatButtonDisabled: false});
	}

  //differentiate between phrases which have already been read, phrases which
  // are currently being read, and phrases which will be read in the future
  // so that we can style them differently
  getPhraseClass(index) {
    let phraseClass = "phrase";
    let isActive = false;

    // if we are correcting rather than reading, the the entire passage is current
    if(this.props.isCorrection){
      return phraseClass + " current";
    }

    if(index < this.props.currentPhraseIndex){
      phraseClass += " done";
    } else if(index === this.props.currentPhraseIndex){
      phraseClass += " current";
      isActive = true;
    } else {
      phraseClass += " future";
    }
    return {phraseClass:phraseClass, isActive:isActive};
  }

  getCurrentPhraseMetaData(phraseIndex) {
    const prevPhrases = this.props.pageIndex * this.props.phrases.length;
    const overallIndex = prevPhrases + phraseIndex;
    if (this.props.story?.items?.[overallIndex]) {
      return this.props.story.items[overallIndex];
    }
    return {};
  }

  // create the phrase ui elements.
  getPhrases() {
    return this.props.phrases.map((phrase, index) => {

      let highlightedWord = -1;
      if(index === this.props.currentPhraseIndex){
        highlightedWord = this.props.interventionWordIndex >= 0 ? this.props.interventionWordIndex : this.props.currentWordIndex;
      }

      let {pageIndex, scores} = this.props;
      let phraseScore = scores && scores[pageIndex] ? (scores[pageIndex][index] || {}): {};
      let phraseErrors = phraseScore.totalErrors || [];
      let isFinal = this.props.isFinal || (index !== this.props.currentPhraseIndex);
      let phraseMeta = this.getPhraseClass(index);

      const phraseKeyPrefix = this.props.shouldUseAOS && this.props.story && this.props.story.storyId ? this.props.story.storyId : '';

      return (
        <Phrase
          className={phraseMeta.phraseClass}
          currentPageIndex={pageIndex}
          currentPhraseIndex={this.props.currentPhraseIndex}
          isActive={phraseMeta.isActive}
          text={phrase}
          isNextPhraseVideo={this.props.isNextPhraseVideo}
          phraseMetaData={this.getCurrentPhraseMetaData(index)}
          isListening={this.props.isListening}
          waitingForAsrConnection={this.props.waitingForAsrConnection}
          endOfPhrase={this.props.endOfPhrase}
          modalOpen={this.props.modalOpen}
          assessmentMode={this.props.assessmentMode}
          currentWordIndex={this.props.currentWordIndex}
          final={isFinal}
          errors={phraseErrors}
          isCursorFree={this.props.isCursorFree}
          isTapToRead={this.props.isTapToRead}
          onWordTapped={this.props.onWordTapped}
          showNextButton={this.props.showNextButton}
          onNextButtonClick={this.props.onNextButtonClick}
          highlightedWord={highlightedWord}
          interventionWordIndex={this.props.interventionWords ? this.props.interventionWords[index] : -1}
          key={phraseKeyPrefix + ((this.props.phraseMetaData && this.props.phraseMetaData.type == "video") ? "theOnlyDyslexiaVideoPhrase" : "phrase" + index + pageIndex)}
          isCorrection={this.props.isCorrection}
          setErrors={this.props.setErrors}
          setDemoClick={this.props.setDemoClick}
          setPlacement={this.props.setPlacement}
          config={this.props.config}
          speech={this.props.speech}
          startPhrase={this.props.startPhrase}
          nonAudioStartPhrase={this.props.nonAudioStartPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          endPhrase={this.props.endPhrase}
          setTextResponse={this.props.setTextResponse}
          setEncodedResponse={this.props.setEncodedResponse}
          setArrayResponse={this.props.setArrayResponse}
          isRepeating={this.state.isRepeating}
          handleRepeatInstructions={this.repeatPhraseInstructions.bind(this)}
          sendMessage={this.props.sendMessage}
          onSubmitQuiz={this.props.onSubmitQuiz}
          flaggedWords={this.props.flaggedWords}
          phraseWordStylingEnabled={this.props.phraseWordStylingEnabled}
          styleUnscoredWords={this.props.styleUnscoredWords}
          phraseBreaks={this.props.phraseBreaks}
          skipIntros={this.props.skipIntros}
          activityIndex={this.props.activityIndex}
          disablePictures={this.props.disablePictures}
          transcript={this.props.transcript}
          isEarlyReader={this.props.isEarlyReader}
          storyLength={this.props.storyLength}
          paused={this.props.paused}
          handleDefaultPauseDetected={this.props.handleDefaultPauseDetected}
          doneHandlingPause={this.props.doneHandlingPause}
          advanceOneWord={this.props.advanceOneWord}
          setDisableInterventionsForPhrase = {this.props.setDisableInterventionsForPhrase}
          setAllowFirstWordSkipOnPause = {this.props.setAllowFirstWordSkipOnPause}
          skipWord = {this.props.skipWord}
          triggerWordIntervention = {this.props.triggerWordIntervention}
          story={this.props.story}
          repeatInstructions={this.props.repeatInstructions}
          setHideRepeatButton={this.setHideRepeatButton.bind(this)}
          setHideListeningStatus={this.setHideListeningStatus.bind(this)}
          disableRepeatButton={this.disableRepeatButton.bind(this)}
          enableRepeatButton={this.enableRepeatButton.bind(this)}
          setRecordingProps={this.props.setRecordingProps}
          shouldUseAOS={this.props.shouldUseAOS}
          configuredToExtendAssessmentTimeout={this.props.configuredToExtendAssessmentTimeout}
          assessmentTimeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
          isFirstAssessmentItemOfType={this.props.isFirstAssessmentItemOfType}
          beginOverlayRepeatPlayback={this.beginOverlayRepeatPlayback.bind(this)}
          endOverlayRepeatPlayback={this.endOverlayRepeatPlayback.bind(this)}
          onVerbalIntroductionsComplete={this.props.onVerbalIntroductionsComplete}
          />
      );
    });
  }

  /** Listening comprehension testlet overlay replay — mirrors repeatPhraseInstructions timers without repeatButtonDisabled guard. */
  beginOverlayRepeatPlayback() {
    if (this.state.isRepeating) return false;
    this.props.onInstructionStart?.();
    this.setState({ isRepeating: true });
    return true;
  }

  endOverlayRepeatPlayback() {
    this.setState({ isRepeating: false });
    this.props.onInstructionEnd?.();
  }

  //TODO: abstract this and move it elsewhere
  repeatPhraseInstructions(){
    if(this.state.isRepeating || this.state.repeatButtonDisabled){
      return;
    }
    if(this.props.repeatInstructions){
      this.props.onInstructionStart();
      this.setState({isRepeating: true}, () => {
        let onEnd = () => {
          this.setState({isRepeating: false});
          this.props.onInstructionEnd();
        };
        // Shared repeat handler: MMC/Bucketing/Sequencing delegate their own repeat buttons
        // here. Evaluar markup instructions (blending/GPC), or any repeatVariation carrying a
        // <phoneme> tag, route through the pre-recorded player instead of TTS-ing raw markup
        // (a self-closing phoneme tag has no inner text, so TTS drops the sound entirely).
        // abortSpeech bumps the shared abort-generation counter the idle-nudge loop checks,
        // so a tap cleanly stops an in-flight idle nudge instead of overlapping.
        if(isMarkupInstructionForStory(this.props.story) || stringContainsHumanPhoneme(this.props.repeatInstructions)){
          this.props.speech.abortSpeech();
          playMarkupSequence(this.props.speech, this.props.repeatInstructions, { locale: getStoryContentLocale(this.props.story) })
            .then(onEnd)
            .catch((err) => {
              logSessionInfo('reader_repeat_markup_sequence_failed', { message: err?.message });
              onEnd();
            });
          return;
        }
        if(getUserData().bilingualMode){
          let parsedInstructions = parseSkipTranslationTags(this.props.repeatInstructions);
          let bilingualSequenceToSpeak = {
            textToSpeak: parsedInstructions,
            passThroughSpeechTags: true,
            onEnd: onEnd,
          }
          this.props.speech.speakSequence(bilingualSequenceToSpeak);
        } else {
          let instructionSpeech = {
            textToSpeak: this.props.repeatInstructions,
            passThroughSpeechTags: true,
            onEnd: onEnd,
          }
          this.props.speech.speak(instructionSpeech);
        }
      });
    }
  }

  getListeningStatusClasses(forceHide){
    return !this.props.listeningStatusVisible || forceHide || this.state.hideListeningStatus ? "hidden" : "";
  }

  render(){
    let title = this.props.storyTitle;
    let titleClass = "title"
    if(this.props.author && this.props.author != " "){
      title += " by " + this.props.author;
    }
    if(this.props.phraseMetaData && this.props.phraseMetaData.writtenInstructions){
      title = this.props.phraseMetaData.writtenInstructions;
      titleClass += " instructions";
    }

    let repeatClass = "repeatButton";
    if(this.state.isRepeating || this.state.repeatButtonDisabled){
      repeatClass += " disabled";
    }

    const storyMetadataType = getMetadataType(this.props.phraseMetaData)
    const readerClassnames = classNames({
      "reader": true,
      "inner": true,
      "paged-story": get(this.props, 'pageIndex') !== undefined,
      [storyMetadataType]: storyMetadataType.toLowerCase(),
      // TT-1006: one-phrase-per-screen + larger font/spacing layout for tap-to-read.
      "tapToRead": this.props.isTapToRead,
      hidden: this.props.screenContentOverride,
    })

    //TODO: fix this reader className kerfuffle
    return (
      <VirtualScreen
        pageIndex={this.props.pageIndex}
        storyLength={this.props.storyLength}
        isCorrection={this.props.isCorrection}
        listeningStatusEnabled={this.props.listeningStatusEnabled}
        listeningStatusVisible={this.getListeningStatusClasses(this.props.screenContentOverride)} >
        <div className={readerClassnames}>
          {this.props.repeatInstructions && !this.state.hideRepeatButton && !this.props.showingTutorFTUE &&
            <div className={repeatClass} {...doNotUseAccessibleStaticElementInteractions({onClick: this.repeatPhraseInstructions.bind(this)})}>
              <FaVolumeUp size="24" className="repeatButtonSvg"/>
            </div>
          }
          {this.props.showingTutorFTUE ? this.props.tutorFTUEComponent : this.getPhrases()}
          <NextPageButton shouldHaveNextPageButton={this.props.shouldHaveNextPageButton}
            onNextPageClick={this.props.onNextPageClick}
            pageNavEnabled={this.props.pageNavEnabled} />
          {this.props.isTapToRead && this.props.showNextButton &&
            // Tap-to-read end-of-phrase: the designed next button (TT-1139) — the same teal
            // chevron pill used by the no-cursor experience — wired to the end-of-phrase handler.
            // Intentionally NOT the shared <Button type="next"> used by NextPageButton, so page
            // navigation styling is unaffected.
            // TODO(TT-1007): add the 3s delay + last-word highlight parity.
            <div className="pageNavigation">
              <NextButton onClick={this.props.onNextButtonClick} />
            </div>
          }
          <Branding brand={this.props.storyBrand}/>
          <ReaderVideoPlayer speech={this.props.speech}/>
        </div>
        {this.props.screenContentOverride}
      </VirtualScreen>
    );
  }
}